import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { useDispatch, useSelector } from 'react-redux'
import { selectHash } from '@selectors'

import { submitCardAction } from '@redux/actions/priority'

import Button from '@components/button/button.component'

import { WHITE } from '@omnilert/components'
import { SET_CONFIRMATION_MODAL } from '../../redux/actions/types'

import i18n from 'i18n-js'

import { BUTTON_OK } from '../../assets/i18n/strings'

import useStyles from './base-action-button.style'

const BaseActionButton = (props) => {
  const { buttonState, className, children, documentId, data, onClick, pill } = props

  const classes = useStyles({ pill })

  const pac = useSelector(selectHash)
  
  const getButtonClasses = useMemo(() => {
    const buttonClasses = [classes.baseActionButton]

    if (pill) buttonClasses.push(classes.pill)

    if (!pill) buttonClasses.push(classes.default)

    return classNames(buttonClasses, { [className]: !!className })
  },
  [
    classes.baseActionButton,
    classes.default,
    classes.pill,
    className,
    pill
  ])

  const getWrapperClasses = useMemo(() => {
    const wrapperClasses = []

    if (pill) wrapperClasses.push(classes.wrapperPill)

    if (!pill) wrapperClasses.push(classes.wrapperDefault)

    return classNames(wrapperClasses)
  },
  [
    classes.wrapperDefault,
    classes.wrapperPill,
    pill
  ])

  const {
    confirmationText,
    confirmationTitle,
    headers,
    method,
    postData,
    showConfirmation,
    url
  } = data

  const dispatch = useDispatch()

  const performAction = () => {
    if (buttonState !== 'selectable') return

    onClick()

    const actionData = {
      ...postData,
      personal_access_code: pac
    }

    const payload = {
      request: {
        data: actionData,
        headers,
        method,
        url
      },
      setLoading: {
        meta: {
          actionId: actionData.action_id,
          documentId
        }
      }
    }

    dispatch(submitCardAction({ payload }))

    handleOnDismiss()
  }

  const handleClick = () => {
    if (showConfirmation) {
      dispatch({
        type: SET_CONFIRMATION_MODAL,
        payload: {
          isCancellable: true,
          confirmText: i18n.t(BUTTON_OK),
          onConfirm: performAction,
          onDismiss: handleOnDismiss,
          iconName: 'check',
          iconVariant: 'success',
          isVisible: true,
          bodyText: [
            {
              color: WHITE,
              size: 'lg',
              text: confirmationText
            }
          ],
          title: confirmationTitle
        }
      })
    } else {
      performAction()
    }
  }

  const handleOnDismiss = () => {
    dispatch({
      type: SET_CONFIRMATION_MODAL,
      payload: {
        isVisible: false
      }
    })
  }

  return (
    <Button
      backgroundColor='transparent'
      className={getButtonClasses}
      onClick={handleClick}
    >
      <div className={getWrapperClasses}>
        {children}
      </div>
    </Button>
  )
}

BaseActionButton.defaultProps = {
  buttonState: 'selectable',
  className: '',
  data: null,
  documentId: '',
  onClick: () => {},
  pill: false
}

BaseActionButton.propTypes = {
  buttonState: PropTypes.oneOf(['disabled', 'loading', 'selectable', 'selected']),
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
  documentId: PropTypes.string,
  onClick: PropTypes.func,
  pill: PropTypes.bool
}

export default BaseActionButton

